import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import {
  getProductos, getDashboardStock, getForecast,
  registrarCompra, ajustarStock, getProveedores, crearProducto,
  pausarProducto, borrarProducto, restaurarProducto, getKitsQueUsan, ESTADO_STOCK,
  fichaCargadaOK, actualizarProducto
} from '../../lib/stockApi'
import { reducirImagen } from '../../lib/imageResize'
import { CATEGORIAS_ADMIN, PREFIJOS_SKU_POR_CATEGORIA } from '../../lib/categoriasProducto'
import ExportButton from '../../components/admin/ExportButton'
import { fmt } from '../../lib/exportToCsv'

// Columnas para exportar productos a CSV/Sheets con TODA la info contable.
// Calcula precios s/IVA, márgenes, markup, diferencia vs mercado. Anti-NaN: si falta data, vacío.
const COLUMNAS_PRODUCTOS = [
  { key: 'sku',                   label: 'SKU' },
  { key: 'nombre',                label: 'Nombre' },
  { key: 'marca',                 label: 'Marca' },
  { key: 'presentacion',          label: 'Presentación' },
  { key: 'categoria',             label: 'Categoría' },
  { key: 'tier',                  label: 'Tier' },
  { key: 'proveedor.nombre',      label: 'Proveedor' },
  { key: 'activo',                label: 'Activo',           format: fmt.bool },
  { key: 'publicado',             label: 'Publicado',        format: fmt.bool },
  { key: 'vendible_suelto',       label: 'Vendible suelto',  format: fmt.bool },
  { key: 'pausado',               label: 'Pausado',          format: fmt.bool },
  { key: 'pausado_motivo',        label: 'Motivo pausa' },
  // Stock
  { key: 'stock_actual',          label: 'Stock actual',     format: fmt.int },
  { key: 'stock_reservado',       label: 'Stock reservado',  format: fmt.int },
  { key: '',                      label: 'Stock disponible', format: (_, r) => fmt.int(Number(r.stock_actual || 0) - Number(r.stock_reservado || 0)) },
  { key: 'stock_minimo',          label: 'Stock mínimo',     format: fmt.int },
  { key: 'stock_critico',         label: 'Stock crítico',    format: fmt.int },
  { key: 'forecast.estado_stock', label: 'Estado stock' },
  { key: 'forecast.dias_restantes', label: 'Días de stock',  format: fmt.int },
  // Costos / IVA
  { key: 'costo_unitario',        label: 'Costo s/IVA',      format: fmt.uyu },
  { key: 'iva_pct',               label: 'IVA %',            format: (v) => v != null ? `${v}%` : '' },
  { key: '',                      label: 'Costo c/IVA',      format: (_, r) => {
    const c = Number(r.costo_unitario || 0); const iva = Number(r.iva_pct || 0)
    return c ? fmt.uyu(c * (1 + iva/100)) : ''
  } },
  // Precios
  { key: 'precio_publico',        label: 'Precio venta c/IVA', format: fmt.uyu },
  { key: '',                      label: 'Precio venta s/IVA', format: (_, r) => {
    const p = Number(r.precio_publico || 0); const iva = Number(r.iva_pct || 0)
    return p && iva ? fmt.uyu(p / (1 + iva/100)) : (p ? fmt.uyu(p) : '')
  } },
  { key: '',                      label: 'IVA débito venta',   format: (_, r) => {
    const p = Number(r.precio_publico || 0); const iva = Number(r.iva_pct || 0)
    return p && iva ? fmt.uyu(p - p / (1 + iva/100)) : ''
  } },
  // Márgenes s/IVA (es como se piensa la rentabilidad en Limpack)
  { key: '',                      label: 'Margen UYU s/IVA', format: (_, r) => {
    const c = Number(r.costo_unitario || 0); const p = Number(r.precio_publico || 0); const iva = Number(r.iva_pct || 0)
    if (!c || !p) return ''
    const ps = iva ? p / (1 + iva/100) : p
    return fmt.uyu(ps - c)
  } },
  { key: '',                      label: 'Margen % sobre costo', format: (_, r) => {
    const c = Number(r.costo_unitario || 0); const p = Number(r.precio_publico || 0); const iva = Number(r.iva_pct || 0)
    if (!c || !p) return ''
    const ps = iva ? p / (1 + iva/100) : p
    return fmt.pctRaw(((ps - c) / c) * 100)
  } },
  { key: '',                      label: 'Margen % sobre venta', format: (_, r) => {
    const c = Number(r.costo_unitario || 0); const p = Number(r.precio_publico || 0); const iva = Number(r.iva_pct || 0)
    if (!c || !p) return ''
    const ps = iva ? p / (1 + iva/100) : p
    return fmt.pctRaw(((ps - c) / ps) * 100)
  } },
  // Mercado / posicionamiento
  { key: 'precio_mercado',        label: 'Precio mercado (ref)', format: fmt.uyu },
  { key: '',                      label: 'Dif % vs mercado',     format: (_, r) => {
    const p = Number(r.precio_publico || 0); const m = Number(r.precio_mercado || 0)
    if (!p || !m) return ''
    return fmt.pctRaw(((p - m) / m) * 100)
  } },
  // Datos del producto
  { key: 'aroma',                 label: 'Aroma' },
  { key: 'rendimiento',           label: 'Rendimiento' },
  { key: 'ideal_para',            label: 'Ideal para' },
  { key: 'grupo_equivalencia',    label: 'Grupo equivalencia' },
  { key: 'notas',                 label: 'Notas' },
  { key: 'updated_at',            label: 'Actualizado',      format: fmt.date },
]

export default function Stock() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Fix 7-jul-2026 (Nico): aceptar ?filtro=critico desde deep-link del dashboard
  const filtroInicial = searchParams.get('filtro') || 'todos'
  const [productos, setProductos] = useState([])
  const [forecast, setForecast] = useState([])
  const [stats, setStats] = useState(null)
  const [proveedores, setProveedores] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState(filtroInicial)
  const [compraModal, setCompraModal] = useState(null)
  const [ajusteModal, setAjusteModal] = useState(null)
  const [nuevoModal, setNuevoModal] = useState(false)
  const [pausaModal, setPausaModal] = useState(null)  // producto a pausar
  const [busqueda, setBusqueda] = useState('')
  const [filtroProveedor, setFiltroProveedor] = useState('')
  const [filtroCategoria, setFiltroCategoria] = useState('')
  const [filtroTier, setFiltroTier] = useState('')
  const [verPausados, setVerPausados] = useState(false)
  const [verEliminados, setVerEliminados] = useState(false)

  useEffect(() => { load() }, [verEliminados])

  async function load() {
    setLoading(true)
    const [p, f, s, prov] = await Promise.all([
      getProductos({ incluirEliminados: verEliminados }), getForecast(), getDashboardStock(), getProveedores()
    ])
    setProductos(p); setForecast(f); setStats(s); setProveedores(prov)
    setLoading(false)
  }

  const productosConForecast = useMemo(() => {
    const fmap = Object.fromEntries(forecast.map(f => [f.producto_id, f]))
    return productos.map(p => ({ ...p, forecast: fmap[p.id] }))
  }, [productos, forecast])

  const filtrados = useMemo(() => {
    let f = productosConForecast
    // Filtro estado stock (chips arriba). Fix 7-jul-2026 (Nico): estado_stock
    // solo tiene ok/bajo/critico. "no_alcanza_7d" es un pseudo-filtro que aplica
    // sobre sobrante_7d < 0.
    if (filtro === 'no_alcanza_7d') {
      f = f.filter(p => Number(p.forecast?.sobrante_7d ?? 0) < 0)
    } else if (filtro !== 'todos') {
      f = f.filter(p => p.forecast?.estado_stock === filtro)
    }
    // Filtro pausados (no aplica si estás viendo eliminados — todos están pausados)
    if (!verPausados && !verEliminados) f = f.filter(p => !p.pausado)
    // Filtros adicionales
    if (filtroProveedor) f = f.filter(p => p.proveedor?.id === filtroProveedor)
    if (filtroCategoria) f = f.filter(p => p.categoria === filtroCategoria)
    if (filtroTier)      f = f.filter(p => p.tier === filtroTier)
    // Búsqueda por texto (sku/nombre/marca)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      f = f.filter(p =>
        (p.sku || '').toLowerCase().includes(q) ||
        (p.nombre || '').toLowerCase().includes(q) ||
        (p.marca || '').toLowerCase().includes(q)
      )
    }
    return f
  }, [productosConForecast, filtro, busqueda, filtroProveedor, filtroCategoria, filtroTier, verPausados])

  const pausadosCount = productosConForecast.filter(p => p.pausado && !p.deleted_at).length
  const eliminadosCount = productosConForecast.filter(p => p.deleted_at).length

  return (
    <AdminLayout>
      <style>{`
        /* ════ Stock rediseño v2 — scoped para no afectar otras páginas ════ */
        .stock-redesign-v2 .t-table {
          background: #fff;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          /* Fix 7-jul-2026 (Nico): era 'overflow: hidden' → cortaba el dropdown ⋯
             de las últimas filas. Ahora el menú puede salir del container. */
          overflow: visible;
          font-size: 13px;
        }
        .stock-redesign-v2 .t-table thead th {
          background: #fff;
          color: #64748B;
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 10px 14px;
          border-bottom: 1px solid #E2E8F0;
          text-align: left;
          white-space: nowrap;
        }
        .stock-redesign-v2 .t-table tbody tr {
          border-top: 1px solid #F1F5F9;
          transition: background 0.08s;
        }
        .stock-redesign-v2 .t-table tbody tr:hover { background: #FAFBFC; }
        .stock-redesign-v2 .t-table tbody td {
          padding: 0 14px;
          height: 60px;
          vertical-align: middle;
          color: #0F172A;
        }
        /* SKU mono teal sutil */
        .stock-redesign-v2 .sku-cell {
          font-family: 'SF Mono', Menlo, Consolas, monospace;
          font-size: 11.5px;
          color: #00A8B5;
          font-weight: 600;
          letter-spacing: 0;
        }
        /* Números tabulares */
        .stock-redesign-v2 .num-mono {
          font-family: 'SF Mono', Menlo, Consolas, monospace;
          font-variant-numeric: tabular-nums;
          font-weight: 600;
        }
        /* Estado dot */
        .stock-redesign-v2 .estado-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 12px;
          font-weight: 500;
          color: #475569;
        }
        .stock-redesign-v2 .estado-pill .dot {
          width: 7px; height: 7px; border-radius: 50%;
          flex-shrink: 0;
        }
        .stock-redesign-v2 .estado-pill .dot.ok { background: #10B981; box-shadow: 0 0 0 3px rgba(16,185,129,0.12); }
        .stock-redesign-v2 .estado-pill .dot.bajo { background: #F59E0B; box-shadow: 0 0 0 3px rgba(245,158,11,0.12); }
        .stock-redesign-v2 .estado-pill .dot.crit { background: #EF4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.12); animation: stockPulse 2s ease-in-out infinite; }
        @keyframes stockPulse { 0%,100% { box-shadow: 0 0 0 3px rgba(239,68,68,0.12); } 50% { box-shadow: 0 0 0 5px rgba(239,68,68,0.18); } }
        /* Cards KPI más sutiles */
        .stock-redesign-v2 .cards-grid {
          background: #fff;
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          overflow: hidden;
          display: flex;
          gap: 0;
          margin-top: 14px;
        }
        .stock-redesign-v2 .cards-grid .card {
          flex: 1;
          border-radius: 0;
          border: none;
          border-right: 1px solid #E2E8F0;
          background: transparent;
          padding: 14px 18px;
          box-shadow: none;
        }
        .stock-redesign-v2 .cards-grid .card:last-child { border-right: none; }
        .stock-redesign-v2 .cards-grid .card .label {
          font-size: 10.5px;
          color: #64748B;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          font-weight: 600;
        }
        .stock-redesign-v2 .cards-grid .card .stat {
          font-size: 22px;
          font-weight: 700;
          margin-top: 4px;
          letter-spacing: -0.02em;
          font-variant-numeric: tabular-nums;
          font-family: 'SF Mono', monospace;
        }
        /* Stock prod link más sutil */
        .stock-redesign-v2 .stock-prod-link { text-decoration: none; }
        .stock-redesign-v2 .stock-prod-link:hover span:first-child { color: #00A8B5; }

        /* Anchos fijos + ellipsis para que las filas no rompan */
        /* Fix 7-jul-2026 (Nico): headers se solapaban porque los widths eran
           menores que el ancho del texto del header. Ampliados para que entre
           "DISPONIBLE" "NECESARIO 7D" "ESTADO" sin cortar. */
        .stock-redesign-v2 .t-table { table-layout: fixed; width: 100%; min-width: 1180px; }
        .stock-redesign-v2 .t-table thead th:nth-child(1)  { width: 62px; }    /* SKU */
        .stock-redesign-v2 .t-table thead th:nth-child(2)  { width: auto; min-width: 240px; }   /* Producto */
        .stock-redesign-v2 .t-table thead th:nth-child(3)  { width: 80px; }    /* Pres. */
        .stock-redesign-v2 .t-table thead th:nth-child(4)  { width: 90px; }    /* Proveedor */
        .stock-redesign-v2 .t-table thead th:nth-child(5)  { width: 68px; }    /* Stock */
        .stock-redesign-v2 .t-table thead th:nth-child(6)  { width: 50px; }    /* 7d */
        .stock-redesign-v2 .t-table thead th:nth-child(7)  { width: 92px; }    /* Estado */
        .stock-redesign-v2 .t-table thead th:nth-child(8)  { width: 88px; }    /* Costo */
        .stock-redesign-v2 .t-table thead th:nth-child(9)  { width: 148px; }   /* Canales */
        .stock-redesign-v2 .t-table thead th:nth-child(10) { width: 74px; }    /* Orden */
        .stock-redesign-v2 .t-table thead th:nth-child(11) { width: 44px; }    /* Acciones */
        /* Wrapper con scroll horizontal para mobile — antes la tabla se aplastaba */
        .stock-redesign-v2 .t-wrap { overflow-x: auto; }

        /* Truncar contenido de celdas Pres + Proveedor con ellipsis */
        .stock-redesign-v2 .t-table tbody td:nth-child(3),
        .stock-redesign-v2 .t-table tbody td:nth-child(4) {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        /* Producto: nombre con max 2 líneas + ellipsis si entra */
        .stock-redesign-v2 .t-table tbody td:nth-child(2) { overflow: hidden; padding-top: 8px; padding-bottom: 8px; }
        .stock-redesign-v2 .stock-prod-link > div:first-child { flex-wrap: nowrap; min-width: 0; }
        .stock-redesign-v2 .stock-prod-link > div:first-child > span:first-child {
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          white-space: normal;
          line-height: 1.25;
          font-size: 12.8px;
        }
        /* Si el nombre tomó 2 líneas, marca/categoría va abajo igual */
        .stock-redesign-v2 .stock-prod-link > div:nth-child(2) {
          margin-top: 2px;
          font-size: 10.5px !important;
          line-height: 1.3;
        }

        /* Canales: 3 chips (Tienda · ML · ★) */
        .stock-redesign-v2 .canales-cell {
          display: flex; align-items: center; gap: 4px;
        }
        .stock-redesign-v2 .canal-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          border-radius: 5px;
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0;
          color: #CBD5E1;
          background: #fff;
          border: 1px solid #E2E8F0;
          line-height: 1.4;
          white-space: nowrap;
          cursor: pointer;
          font-family: inherit;
          text-decoration: none;
          transition: all 0.1s;
        }
        .stock-redesign-v2 .canal-chip:hover { border-color: #94A3B8; }
        .stock-redesign-v2 .canal-chip:disabled { cursor: not-allowed; opacity: 0.4; }
        .stock-redesign-v2 .canal-chip .dot {
          width: 5px; height: 5px; border-radius: 50%;
          background: #CBD5E1;
          display: inline-block;
        }
        /* Tienda — verde cuando publicado */
        .stock-redesign-v2 .canal-tienda.on {
          color: #065F46;
          border-color: rgba(16,185,129,0.4);
          background: rgba(16,185,129,0.08);
        }
        .stock-redesign-v2 .canal-tienda.on .dot { background: #10B981; }
        .stock-redesign-v2 .canal-tienda.warn {
          color: #B45309;
          border-color: rgba(245,158,11,0.4);
          background: rgba(245,158,11,0.08);
        }
        .stock-redesign-v2 .canal-tienda.warn .dot { background: #F59E0B; }
        /* Solo ML — chip tienda gris con guion + texto naranja sutil */
        .stock-redesign-v2 .canal-tienda.solo-ml {
          color: #9A3412;
          border-color: rgba(234,88,12,0.25);
          background: rgba(255,247,237,0.5);
          text-decoration: line-through;
          text-decoration-color: rgba(234,88,12,0.4);
          text-decoration-thickness: 1px;
        }
        .stock-redesign-v2 .canal-tienda.solo-ml .dot { background: #FB923C; }
        /* ML — verde MELI cuando LIVE (active) · azul cuando paused · amarillo cuando draft */
        .stock-redesign-v2 .canal-ml.on {
          color: #0F766E;
          border-color: rgba(0,168,181,0.45);
          background: rgba(0,168,181,0.10);
        }
        .stock-redesign-v2 .canal-ml.on .dot { background: #00A8B5; }
        .stock-redesign-v2 .canal-ml.paused {
          color: #1E40AF;
          border-color: rgba(59,130,246,0.4);
          background: rgba(59,130,246,0.08);
        }
        .stock-redesign-v2 .canal-ml.paused .dot { background: #3B82F6; }
        .stock-redesign-v2 .canal-ml.draft {
          color: #92400E;
          border-color: rgba(245,158,11,0.35);
          background: rgba(245,158,11,0.08);
        }
        .stock-redesign-v2 .canal-ml.draft .dot { background: #F59E0B; }
        /* Destacado — amber sólo estrella */
        .stock-redesign-v2 .canal-dst {
          padding: 3px 7px;
          font-size: 13px;
          color: #CBD5E1;
        }
        .stock-redesign-v2 .canal-dst.on {
          color: #92400E;
          border-color: rgba(245,158,11,0.4);
          background: rgba(245,158,11,0.1);
        }
      `}</style>
      <div className="page stock-redesign-v2">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap: 12, flexWrap:'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0 }}>Stock</h1>
            <p className="lead" style={{ margin:'4px 0 0' }}>Inventario · forecast 7 días · alertas automáticas</p>
          </div>
          <ExportButton
            data={filtrados}
            columns={COLUMNAS_PRODUCTOS}
            filename="productos"
            label="Bajar a Sheets"
          />
        </div>
        <div className="cards-grid">
          <div className="card">
            <h3>Total productos</h3>
            <div className="stat">{stats?.total_productos ?? '—'}</div>
          </div>
          <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #EF4444' }} onClick={() => setFiltro('critico')}>
            <h3>Críticos</h3>
            <div className="stat" style={{ color: '#EF4444' }}>{stats?.productos_criticos ?? '—'}</div>
          </div>
          <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #F59E0B' }} onClick={() => setFiltro('bajo')}>
            <h3>Bajos</h3>
            <div className="stat" style={{ color: '#F59E0B' }}>{stats?.productos_bajos ?? '—'}</div>
          </div>
          <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid #7C3AED' }} onClick={() => setFiltro('no_alcanza')}>
            <h3>No alcanzan 7d</h3>
            <div className="stat" style={{ color: '#7C3AED' }}>{stats?.no_alcanzan_7d ?? '—'}</div>
          </div>
          <div className="card">
            <h3>Valor inventario</h3>
            <div className="stat">${Number(stats?.valor_inventario ?? 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
          </div>
        </div>

        {/* Fix 7-jul-2026 (Nico): chips visibles para filtrar por estado stock.
            Antes solo se podía filtrar via URL param — no había UI. Ahora click
            en cada chip cambia el state 'filtro' y se refleja en la tabla. */}
        <div className="btn-row" style={{ marginTop: 20, flexWrap: 'wrap' }}>
          <button className="btn primary" onClick={() => setNuevoModal(true)}>+ Nuevo producto</button>
          <button className={`btn ${filtro === 'todos' ? 'primary' : 'ghost'}`} onClick={() => setFiltro('todos')}>
            Todos ({productos.length})
          </button>
          <button
            className={`btn ${filtro === 'critico' ? 'primary' : 'ghost'}`}
            onClick={() => setFiltro('critico')}
            style={filtro === 'critico' ? { background: '#DC2626', borderColor: '#DC2626', color: '#fff' } : { color: '#DC2626', borderColor: '#FCA5A5' }}
          >
            🔴 Críticos ({stats?.productos_criticos ?? 0})
          </button>
          <button
            className={`btn ${filtro === 'bajo' ? 'primary' : 'ghost'}`}
            onClick={() => setFiltro('bajo')}
            style={filtro === 'bajo' ? { background: '#D97706', borderColor: '#D97706', color: '#fff' } : { color: '#D97706', borderColor: '#FCD34D' }}
          >
            🟡 Bajos ({stats?.productos_bajos ?? 0})
          </button>
          <button
            className={`btn ${filtro === 'no_alcanza_7d' ? 'primary' : 'ghost'}`}
            onClick={() => setFiltro('no_alcanza_7d')}
            style={filtro === 'no_alcanza_7d' ? { background: '#7C3AED', borderColor: '#7C3AED', color: '#fff' } : { color: '#7C3AED', borderColor: '#C4B5FD' }}
          >
            🟣 No alcanzan 7d ({stats?.no_alcanzan_7d ?? 0})
          </button>
          <button className="btn ghost" onClick={load}>↺ Recargar</button>
        </div>

        {/* Barra de filtros */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(180px, 1fr) minmax(160px, 1fr) minmax(160px, 1fr) minmax(140px, 1fr) auto',
          gap: 10, marginTop: 12, marginBottom: 12,
        }}>
          <input type="search"
            placeholder="🔎 Buscar SKU, nombre o marca…"
            value={busqueda} onChange={e => setBusqueda(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line, #E2E8F0)', fontSize: 13, fontFamily: 'inherit' }} />
          <select value={filtroProveedor} onChange={e => setFiltroProveedor(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line, #E2E8F0)', fontSize: 13, fontFamily: 'inherit', background: '#fff' }}>
            <option value="">📦 Todos los proveedores</option>
            {proveedores.map(pv => <option key={pv.id} value={pv.id}>{pv.nombre}</option>)}
          </select>
          <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line, #E2E8F0)', fontSize: 13, fontFamily: 'inherit', background: '#fff' }}>
            <option value="">🏷️ Todas las categorías</option>
            {CATEGORIAS.map(c => <option key={c.v} value={c.v}>{c.label.split(' ')[0]} {c.label.split(' ').slice(1).join(' ').split('(')[0].trim()}</option>)}
          </select>
          <select value={filtroTier} onChange={e => setFiltroTier(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line, #E2E8F0)', fontSize: 13, fontFamily: 'inherit', background: '#fff' }}>
            <option value="">⭐ Todos los tiers</option>
            <option value="A">A · Premium</option>
            <option value="B">B · Calidad/precio</option>
            <option value="C">C · Marca masiva</option>
            <option value="D">D · Especialidades</option>
          </select>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              border: '1.5px solid', borderColor: verPausados ? '#F59E0B' : 'var(--line, #E2E8F0)',
              borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 700,
              background: verPausados ? '#FEF3C7' : '#fff', color: verPausados ? '#92400E' : 'var(--navy-ink)',
              whiteSpace: 'nowrap',
            }}>
              <input type="checkbox" checked={verPausados} onChange={e => setVerPausados(e.target.checked)} style={{ width: 'auto' }} />
              ⏸️ Pausados ({pausadosCount})
            </label>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              border: '1.5px solid', borderColor: verEliminados ? '#EF4444' : 'var(--line, #E2E8F0)',
              borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 700,
              background: verEliminados ? '#FEE2E2' : '#fff', color: verEliminados ? '#991B1B' : 'var(--navy-ink)',
              whiteSpace: 'nowrap',
            }}>
              <input type="checkbox" checked={verEliminados} onChange={e => setVerEliminados(e.target.checked)} style={{ width: 'auto' }} />
              🗑️ Eliminados ({verEliminados ? eliminadosCount : '?'})
            </label>
          </div>
        </div>
        {(busqueda || filtroProveedor || filtroCategoria || filtroTier) && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            Mostrando <strong>{filtrados.length}</strong> de {productos.length} productos.
            <button onClick={() => { setBusqueda(''); setFiltroProveedor(''); setFiltroCategoria(''); setFiltroTier('') }}
              style={{ background: 'transparent', border: 0, color: 'var(--blue, #1a5cb8)', textDecoration: 'underline', marginLeft: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 }}>
              Limpiar filtros
            </button>
          </div>
        )}

        {loading ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>Cargando...</div>
        ) : (
          <div className="t-wrap">
          <table className="t-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Producto</th>
                <th>Pres.</th>
                <th>Proveedor</th>
                <th style={{ textAlign: 'right' }} title="Stock disponible = físico − reservado por entregas programadas/en ruta">Stock</th>
                <th style={{ textAlign: 'right' }} title="Necesario para los próximos 7 días según ventas y suscripciones">7d</th>
                <th>Estado</th>
                <th style={{ textAlign: 'right' }}>Costo u.</th>
                <th title="Canales: visible en tienda · publicado en ML · destacado">Canales</th>
                <th title="Orden de display en la tienda (menor = más arriba)" style={{ textAlign: 'center' }}>Orden</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(p => {
                const fc = p.forecast || {}
                const est = ESTADO_STOCK[fc.estado_stock] || ESTADO_STOCK.ok
                return (
                  <tr key={p.id} style={p.deleted_at ? { background: '#FEF2F2', opacity: .85 } : p.pausado ? { background: '#FFFBEB' } : {}}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      <Link to={`/admin/stock/${p.sku}`} style={{ color: 'var(--teal)', fontWeight: 700, textDecoration: p.deleted_at ? 'line-through' : 'none' }}>{p.sku}</Link>
                    </td>
                    <td>
                      <Link to={`/admin/stock/${p.sku}`} title="Abrir ficha completa del producto · podés editar precio, stock, peso/dimensiones, envío y todo" style={{ color: 'var(--ink)' }} className="stock-prod-link">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', minWidth: 0 }}>
                          <span style={{ fontWeight: 700, textDecoration: p.deleted_at ? 'line-through' : 'none' }}>{p.nombre}</span>
                          {p.deleted_at && (
                            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, background: '#EF4444', color: '#fff', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>🗑 ELIMINADO</span>
                          )}
                          {p.pausado && !p.deleted_at && (
                            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, background: '#F59E0B', color: '#fff', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>⏸ PAUSADO</span>
                          )}
                          {p.tier && (
                            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, background: '#E0F2FE', color: '#075985', fontWeight: 800, letterSpacing: '.04em' }}>{p.tier}</span>
                          )}
                        </div>
                        {p.marca && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.marca}{p.categoria && ` · ${p.categoria}`}</div>}
                      </Link>
                    </td>
                    <td style={{ fontSize: 13 }}>{p.presentacion}</td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>{p.proveedor?.nombre || '—'}</td>
                    <td className="num" style={{ textAlign: 'right' }}>
                      {(() => {
                        const fisico = Number(p.stock_actual || 0)
                        const reservado = Number(p.stock_reservado || 0)
                        const disponible = fisico - reservado
                        const hayReserva = reservado > 0
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                            <span style={{
                              fontWeight: 800,
                              fontSize: 16,
                              color: hayReserva && disponible <= 0 ? '#EF4444' : 'inherit',
                            }} title={hayReserva ? `Disponible (físico ${fisico.toLocaleString('es-UY')} − reservado ${reservado.toLocaleString('es-UY')})` : 'Stock físico'}>
                              {disponible.toLocaleString('es-UY')}
                            </span>
                            {hayReserva && (
                              <span style={{ fontSize: 10, color: '#7C3AED', fontWeight: 700, letterSpacing: '.03em' }}
                                    title="Reservado por entregas programadas/en ruta">
                                físico {fisico.toLocaleString('es-UY')} · −{reservado.toLocaleString('es-UY')} res
                              </span>
                            )}
                          </div>
                        )
                      })()}
                    </td>
                    <td className="num" style={{ textAlign: 'right', fontSize: 13, color: 'var(--muted)' }}>
                      {Number(fc.necesario_7d || 0).toLocaleString('es-UY')}
                    </td>
                    <td>
                      <span className="estado-pill">
                        <span className={`dot ${fc.estado_stock === 'critico' ? 'crit' : fc.estado_stock === 'bajo' ? 'bajo' : 'ok'}`}></span>
                        <span style={{ color: '#475569', fontWeight: 500, fontSize: 12 }}>{est.label}</span>
                      </span>
                    </td>
                    <td className="num" style={{ textAlign: 'right', fontSize: 13 }}>
                      ${Number(p.costo_unitario).toFixed(2)}
                    </td>
                    <td>
                      {(() => {
                        const sinPrecio = !p.precio_publico || Number(p.precio_publico) <= 0
                        const sinFoto = !p.imagen_url
                        const bloqueado = p.deleted_at || p.pausado
                        const warn = !bloqueado && p.publicado && (sinPrecio || sinFoto)
                        // Fix 15-jul-2026: enML ahora refleja estado real de ml_publicaciones
                        // Estados: active (LIVE) · paused · draft · closed · null (nunca publicado)
                        const mlEstado = p.ml_estado
                        const enML = mlEstado === 'active' || mlEstado === 'paused'
                        const enBorrador = mlEstado === 'draft'
                        const soloMl = !bloqueado && enML && !p.publicado
                        const tooltipML = mlEstado === 'active'
                          ? `LIVE en ML (${p.ml_item_id || 'sin ID'}) — click para ver/editar`
                          : mlEstado === 'paused'
                            ? 'Pausado en ML — click para reanudar'
                            : mlEstado === 'draft'
                              ? 'Borrador — falta terminar el wizard'
                              : mlEstado === 'closed'
                                ? 'Cerrado en ML — click para publicar de nuevo'
                                : 'No publicado en ML — click para publicar'
                        const tooltipTienda = bloqueado
                          ? (p.deleted_at ? 'Producto eliminado' : 'Producto pausado — primero reanudalo')
                          : p.publicado
                            ? `Visible en la tienda — click para ocultar${warn ? ` (⚠ ${sinPrecio ? 'sin precio' : ''}${sinPrecio && sinFoto ? ' y ' : ''}${sinFoto ? 'sin foto' : ''})` : ''}`
                            : soloMl
                              ? 'Solo ML — no aparece en la tienda Limpack · click para publicar también en tienda'
                              : 'Oculto en la tienda — click para publicar'
                        return (
                          <div className="canales-cell">
                            {/* Chip Tienda — toggle publicado/oculto · clase solo-ml cuando es exclusivo ML */}
                            <button
                              type="button"
                              disabled={!!bloqueado}
                              title={tooltipTienda}
                              onClick={async () => {
                                try {
                                  await actualizarProducto(p.id, { publicado: !p.publicado })
                                  load()
                                } catch (e) { alert('Error: ' + e.message) }
                              }}
                              className={`canal-chip canal-tienda ${p.publicado ? 'on' : ''} ${warn ? 'warn' : ''} ${soloMl ? 'solo-ml' : ''}`}
                            >
                              <span className="dot" />
                              Tienda
                            </button>

                            {/* Chip ML — refleja ml_publicaciones.status real (active/paused/draft) */}
                            <Link
                              to={`/admin/canales/mercadolibre/publicar/${p.id}`}
                              title={tooltipML}
                              className={`canal-chip canal-ml ${enML ? 'on' : ''} ${enBorrador ? 'draft' : ''} ${mlEstado === 'paused' ? 'paused' : ''}`}
                            >
                              <span className="dot" />
                              ML
                            </Link>

                            {/* Chip ⭐ Destacado — toggle */}
                            <button
                              type="button"
                              disabled={!!p.deleted_at || !!p.pausado}
                              title={p.destacado ? 'Destacado en la tienda — click para quitar' : 'Click para destacar en la tienda'}
                              onClick={async () => {
                                try {
                                  await actualizarProducto(p.id, { destacado: !p.destacado })
                                  load()
                                } catch (e) { alert('Error: ' + e.message) }
                              }}
                              className={`canal-chip canal-dst ${p.destacado ? 'on' : ''}`}
                            >
                              ★
                            </button>

                            {/* FichaBadge oculto: queda accesible al hacer click en producto */}
                            <span style={{ display: 'none' }}><FichaBadge producto={p} /></span>
                          </div>
                        )
                      })()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="number"
                        value={p.orden ?? 0}
                        disabled={!!p.deleted_at}
                        onChange={async (e) => {
                          const nuevo = parseInt(e.target.value, 10) || 0
                          try {
                            await actualizarProducto(p.id, { orden: nuevo })
                            load()
                          } catch (err) { alert('Error: ' + err.message) }
                        }}
                        style={{
                          width: 55, padding: '4px 6px', fontSize: 12, textAlign: 'center',
                          border: '1px solid #e2e8f0', borderRadius: 6,
                        }}
                        title="Orden de display en la tienda. Menor = más arriba."
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <RowMenu
                        producto={p}
                        onCompra={setCompraModal}
                        onAjustar={setAjusteModal}
                        onPausar={setPausaModal}
                        onReanudar={async (prod) => {
                          if (!confirm(`¿Reanudar ${prod.nombre}? Volverá a aparecer en la Tienda.`)) return
                          try { await pausarProducto(prod.id, false); load() } catch (e) { alert(e.message) }
                        }}
                        onRestaurar={async (prod) => {
                          if (!confirm(`¿Restaurar ${prod.sku} — ${prod.nombre}?\nVuelve a aparecer en el listado y en la Tienda.`)) return
                          try { await restaurarProducto(prod.id); load() } catch (e) { alert(e.message) }
                        }}
                        onBorrar={async (prod) => {
                          if (!confirm(
                            `¿Borrar ${prod.sku} — ${prod.nombre}?\n\n` +
                            `Si tiene compras, kits o movimientos previos, se marca como eliminado (podés restaurarlo).\n` +
                            `Si está limpio, se borra definitivamente.\n\n` +
                            `Esta acción no se puede deshacer en el caso de borrado físico.`
                          )) return
                          try {
                            const res = await borrarProducto(prod.id)
                            if (res?.modo === 'hard_delete') {
                              alert(`✓ ${res.sku} borrado definitivamente.`)
                            } else {
                              alert(`${res.sku} marcado como eliminado.\n${res.movimientos} mov. · ${res.kits} kit(s) · ${res.compras} compra(s) impiden borrarlo del todo.`)
                            }
                            load()
                          } catch (e) { alert('Error al borrar: ' + e.message) }
                        }}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        )}

        {compraModal && (
          <CompraModal producto={compraModal} proveedores={proveedores} onClose={() => setCompraModal(null)} onDone={() => { setCompraModal(null); load() }} />
        )}
        {ajusteModal && (
          <AjusteModal producto={ajusteModal} onClose={() => setAjusteModal(null)} onDone={() => { setAjusteModal(null); load() }} />
        )}
        {nuevoModal && (
          <NuevoProductoModal
            proveedores={proveedores}
            productos={productos}
            onClose={() => setNuevoModal(false)}
            onDone={(sku) => { setNuevoModal(false); navigate(`/admin/stock/${sku}`) }}
          />
        )}
        {pausaModal && (
          <PausaRapidaModal
            producto={pausaModal}
            onClose={() => setPausaModal(null)}
            onDone={() => { setPausaModal(null); load() }}
          />
        )}
      </div>
    </AdminLayout>
  )
}

// Fuente única de verdad en src/lib/categoriasProducto.js
const CATEGORIAS = CATEGORIAS_ADMIN
const TIERS = [
  { v: 'A', label: 'A · Premium (Cottonmax, marcas importadas)' },
  { v: 'B', label: 'B · Calidad/precio (Brilha, Suprema)' },
  { v: 'C', label: 'C · Marca masiva (genérico, supermercado)' },
  { v: 'D', label: 'D · Especialidades (productos de nicho)' },
]

/* ─── Modal pausa rápida desde lista de stock ─── */
function PausaRapidaModal({ producto, onClose, onDone }) {
  const [kits, setKits] = useState([])
  const [loading, setLoading] = useState(true)
  const [motivo, setMotivo] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    getKitsQueUsan(producto.id).then(k => { setKits(k); setLoading(false) })
  }, [producto.id])

  async function pausar() {
    setSaving(true); setErr(null)
    try {
      await pausarProducto(producto.id, true, motivo.trim() || null)
      onDone()
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const totalClientes = kits.reduce((s, k) => s + Number(k.clientes_activos || 0), 0)

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 540, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: 6 }}>⏸️ Pausar producto</h2>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 18 }}>
          <code>{producto.sku}</code> · <strong>{producto.nombre}</strong>
          {producto.marca && <> · {producto.marca}</>}
        </p>

        {loading ? (
          <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 16 }}>Buscando kits afectados…</div>
        ) : kits.length > 0 ? (
          <div style={{ background: '#FEF2F2', border: '2px solid #FCA5A5', borderRadius: 12, padding: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>⚠️</span>
              <div>
                <strong style={{ color: '#7F1D1D', fontSize: 14 }}>Atención: este producto está en {kits.length} kit{kits.length !== 1 ? 's' : ''}</strong>
                <div style={{ fontSize: 12, color: '#991B1B', marginTop: 4 }}>
                  Total <strong>{totalClientes}</strong> cliente{totalClientes !== 1 ? 's' : ''} suscripto{totalClientes !== 1 ? 's' : ''} reciben este producto cada mes. <strong>Tenés que conseguir un sustituto.</strong>
                </div>
              </div>
            </div>
            <ul style={{ paddingLeft: 20, margin: 0, fontSize: 13, color: '#7F1D1D' }}>
              {kits.map(k => (
                <li key={k.kit_codigo} style={{ marginBottom: 4 }}>
                  <strong>{k.kit_nombre}</strong> ({k.kit_tier}) — usa ×{k.cantidad}
                  {k.clientes_activos > 0 && <span> · <strong>{k.clientes_activos}</strong> clientes activos</span>}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: '#166534' }}>
            ✓ Este producto no está en ningún kit. Pausarlo solo lo saca de la Tienda.
          </div>
        )}

        <label style={{ display: 'block', marginBottom: 14 }}>
          <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>
            Motivo (opcional)
          </b>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2}
            placeholder="Ej: Drogería sin stock hasta el lunes 6/5" style={{ width: '100%' }} />
        </label>

        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn primary" onClick={pausar} disabled={saving}
            style={{ background: '#F59E0B', borderColor: '#F59E0B' }}>
            {saving ? 'Pausando…' : '⏸️ Confirmar pausa'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Sugiere el próximo SKU según marca o categoría.
 *  Ej: marca "Brilha Sul" + productos existentes BR01, BR02 → BR03
 *  Si no hay marca, usa categoría: lavarropa → L01, cocina → COC01, etc. */
// Fuente única de verdad en src/lib/categoriasProducto.js
const PREFIJOS_CATEGORIA = PREFIJOS_SKU_POR_CATEGORIA
function sugerirSku(marca, categoria, productosExistentes) {
  let prefijo = ''
  if (marca) {
    // Tomar primeras 2-3 letras alfanuméricas, en mayúscula
    prefijo = marca
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 2)
  }
  if (!prefijo && categoria) {
    prefijo = PREFIJOS_CATEGORIA[categoria] || 'X'
  }
  if (!prefijo) return ''
  // Buscar el max número con ese prefijo
  const regex = new RegExp(`^${prefijo}(\\d+)$`, 'i')
  let max = 0
  productosExistentes.forEach(p => {
    const m = (p.sku || '').match(regex)
    if (m) max = Math.max(max, parseInt(m[1], 10))
  })
  return `${prefijo}${String(max + 1).padStart(2, '0')}`
}

// Helpers IVA: convertir entre precio s/IVA (form) y c/IVA (DB).
function ivaFactor(pct) { return 1 + Number(pct || 22) / 100 }
function toConIva(sinIva, pct) {
  if (sinIva == null || sinIva === '') return null
  return Number((Number(sinIva) * ivaFactor(pct)).toFixed(2))
}
function fmtMontoIva(n) {
  if (n == null || isNaN(n)) return '—'
  return '$' + Number(n).toLocaleString('es-UY', { maximumFractionDigits: 2 })
}

function NuevoProductoModal({ proveedores, productos = [], onClose, onDone }) {
  const [form, setForm] = useState({
    sku: '', nombre: '', marca: '', presentacion: '',
    categoria: '', tier: 'C',
    iva_pct: 22,
    costo_unitario: '', precio_publico: '',
    stock_minimo: 5, stock_critico: 2,
    proveedor_id: '', vendible_suelto: true,
    // Ficha tienda (opcional, también se llena con foto+IA)
    descripcion: '', beneficios: '', modo_uso: '', ideal_para: '',
    composicion: '', aroma: '', rendimiento: '',
  })
  const [mostrarFicha, setMostrarFicha] = useState(false)
  const [foto, setFoto] = useState(null)              // File del producto (la que ve el cliente)
  const [fotoPreview, setFotoPreview] = useState(null)
  // Múltiples fotos de etiqueta (frente, dorso, ingredientes, modo de uso, etc.)
  // Cada item: { file: File, preview: string }
  const [fotosEtiqueta, setFotosEtiqueta] = useState([])
  const [analizando, setAnalizando] = useState(false)
  const [fichaIA, setFichaIA] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  function handleFotoProducto(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setFoto(file)
    setFotoPreview(URL.createObjectURL(file))
  }

  function handleFotosEtiqueta(e) {
    const nuevos = Array.from(e.target.files || [])
    if (!nuevos.length) return
    setFotosEtiqueta(prev => [
      ...prev,
      ...nuevos.map(file => ({ file, preview: URL.createObjectURL(file) })),
    ])
    // Limpiamos el input para poder volver a subir el mismo archivo si se quita
    e.target.value = ''
  }

  function quitarEtiqueta(idx) {
    setFotosEtiqueta(prev => {
      const item = prev[idx]
      if (item?.preview) URL.revokeObjectURL(item.preview)
      return prev.filter((_, i) => i !== idx)
    })
  }

  // Analizar fotos con IA (puede ser solo producto o producto + N etiquetas)
  async function analizar() {
    if (!foto) { setErr('Subí al menos la foto del producto'); return }
    setAnalizando(true); setErr(null)
    try {
      // Reducir CLIENT-SIDE antes de enviar — evita que el backend reviente
      // con fotos del iPad/iPhone de 5-8 MB. Default 1280px, queda en ~700 KB.
      const fotoReducida = await reducirImagen(foto)
      const etiquetasReducidas = await Promise.all(
        fotosEtiqueta.map(it => reducirImagen(it.file))
      )
      const fd = new FormData()
      fd.append('file', fotoReducida)
      etiquetasReducidas.forEach((f, i) => fd.append(`file_etiqueta_${i}`, f))
      const { data: { session } } = await import('../../lib/supabase').then(m => m.supabase.auth.getSession())
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analizar-foto-producto`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: fd,
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
      const f = data.ficha || {}
      setFichaIA(f)
      // Sugerir SKU automático si está vacío
      const skuSugerido = sugerirSku(f.marca, f.categoria, productos)
      setForm(prev => ({
        ...prev,
        sku:          prev.sku || skuSugerido,
        nombre:       f.nombre || prev.nombre,
        marca:        f.marca || prev.marca,
        presentacion: f.presentacion || prev.presentacion,
        categoria:    f.categoria || prev.categoria,
        tier:         f.tier || prev.tier,
      }))
    } catch (e) {
      setErr('No se pudo analizar: ' + e.message)
    } finally { setAnalizando(false) }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!form.sku.trim()) { setErr('Falta SKU'); return }
    if (!form.nombre.trim()) { setErr('Falta nombre'); return }
    if (!form.categoria) { setErr('Falta categoría — necesaria para que aparezca en la Tienda'); return }
    setSaving(true)
    try {
      const beneficiosArr = (form.beneficios || '')
        .split('\n').map(s => s.trim()).filter(Boolean)
      const data = await crearProducto({
        sku: form.sku.trim().toUpperCase(),
        nombre: form.nombre.trim(),
        marca: form.marca.trim() || null,
        presentacion: form.presentacion.trim() || null,
        categoria: form.categoria,
        tier: form.tier,
        iva_pct: Number(form.iva_pct) || 22,
        // Regla c/IVA: guardar tal cual lo que cargó el user (sin convertir)
        costo_unitario: form.costo_unitario === '' || form.costo_unitario == null ? 0 : Number(form.costo_unitario),
        precio_publico: form.precio_publico === '' || form.precio_publico == null ? null : Number(form.precio_publico),
        stock_minimo: parseFloat(form.stock_minimo) || 0,
        stock_critico: parseFloat(form.stock_critico) || 0,
        proveedor_id: form.proveedor_id || null,
        vendible_suelto: form.vendible_suelto,
        // Ficha tienda
        descripcion: form.descripcion.trim() || null,
        beneficios: beneficiosArr,
        modo_uso: form.modo_uso.trim() || null,
        ideal_para: form.ideal_para.trim() || null,
        composicion: form.composicion.trim() || null,
        aroma: form.aroma.trim() || null,
        rendimiento: form.rendimiento.trim() || null,
      })
      // Si hay foto, la subimos via procesar-foto-producto (procesa imagen + sube N etiquetas + carga ficha)
      if (foto && data?.sku) {
        try {
          // Reducir CLIENT-SIDE igual que en analizar()
          const fotoReducida = await reducirImagen(foto)
          const etiquetasReducidas = await Promise.all(
            fotosEtiqueta.map(it => reducirImagen(it.file))
          )
          const fd = new FormData()
          fd.append('file', fotoReducida)
          fd.append('sku', data.sku)
          etiquetasReducidas.forEach((f, i) => fd.append(`file_etiqueta_${i}`, f))
          const { data: { session } } = await import('../../lib/supabase').then(m => m.supabase.auth.getSession())
          const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/procesar-foto-producto`
          await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.access_token || ''}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: fd,
          })
        } catch (_) { /* no bloquear si falla, el user puede subir manual */ }
      }
      onDone(data.sku)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  // Sugerencia de precio mercado a partir del precio público (1.13×)
  const precioPub = parseFloat(form.precio_publico) || 0
  const precioMercadoSugerido = precioPub > 0 ? Math.round((precioPub / 0.88) / 10) * 10 : 0

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 580, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: 6 }}>+ Nuevo producto</h2>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 18 }}>
          Subí la foto del envase y Claude completa los datos automáticamente. Vos revisás y agregás los precios.
        </p>

        {/* Bloque IA: foto producto + N fotos etiqueta + botón analizar */}
        <div style={{ background: '#F8F6F1', border: '2px dashed #00A8B5', borderRadius: 12, padding: 18, marginBottom: 18 }}>
          {/* Foto producto (cliente) — una sola */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#1A3461', marginBottom: 6 }}>
              📸 Foto del producto <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12, alignItems: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {fotoPreview
                  ? <img src={fotoPreview} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <span style={{ fontSize: 32, opacity: .3 }}>📦</span>}
              </div>
              <div>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFotoProducto} disabled={analizando || saving} style={{ width: '100%', fontSize: 11 }} />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
                  La que ve el cliente en la Tienda. Una sola, bien iluminada y de frente.
                </div>
              </div>
            </div>
          </div>

          {/* Fotos etiqueta — múltiples */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#1A3461', marginBottom: 6 }}>
              🏷️ Fotos de la etiqueta <span style={{ color: 'var(--muted)', fontWeight: 500 }}>(opcional, podés subir varias)</span>
              {fotosEtiqueta.length > 0 && (
                <span style={{ marginLeft: 8, background: '#00A8B5', color: '#fff', padding: '1px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800 }}>
                  {fotosEtiqueta.length}
                </span>
              )}
            </label>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
              Subí frente, dorso, ingredientes, modo de uso, etc. Cuantas más, más datos puede sacar la IA.
            </div>

            {fotosEtiqueta.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8, marginBottom: 10 }}>
                {fotosEtiqueta.map((it, i) => (
                  <div key={i} style={{ position: 'relative', background: '#fff', borderRadius: 8, height: 110, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                    <img src={it.preview} alt={`Etiqueta ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button
                      type="button"
                      onClick={() => quitarEtiqueta(i)}
                      disabled={analizando || saving}
                      title="Quitar foto"
                      style={{
                        position: 'absolute', top: 4, right: 4,
                        width: 22, height: 22, borderRadius: '50%',
                        background: 'rgba(26,26,26,.85)', color: '#fff',
                        border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                      }}>×</button>
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      background: 'rgba(26,52,97,.85)', color: '#fff',
                      fontSize: 10, padding: '2px 6px', fontWeight: 700, letterSpacing: '.04em',
                    }}>#{i + 1}</div>
                  </div>
                ))}
              </div>
            )}

            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleFotosEtiqueta}
              disabled={analizando || saving}
              style={{ width: '100%', fontSize: 11 }} />
          </div>

          <button type="button" className="btn primary" onClick={analizar} disabled={analizando || saving || !foto} style={{ marginTop: 14, width: '100%', background: '#1A3461', borderColor: '#1A3461' }}>
            {analizando
              ? '🤖 Analizando con IA…'
              : fichaIA
                ? `✅ Volver a analizar (${1 + fotosEtiqueta.length} foto${fotosEtiqueta.length === 0 ? '' : 's'})`
                : `✨ Analizar fotos con IA${fotosEtiqueta.length > 0 ? ` (1 + ${fotosEtiqueta.length} etiqueta${fotosEtiqueta.length === 1 ? '' : 's'})` : ''}`}
          </button>
          {fichaIA && (
            <div style={{ fontSize: 11, color: '#00A8B5', marginTop: 6, fontWeight: 700, textAlign: 'center' }}>
              ✓ Datos detectados — revisá y ajustá los campos abajo
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <Field label="SKU" required>
              <input type="text" required value={form.sku}
                onChange={e => setForm({...form, sku: e.target.value.toUpperCase()})}
                placeholder={form.marca || form.categoria ? sugerirSku(form.marca, form.categoria, productos) : 'C09'}
                maxLength={20}
                style={{ fontFamily: 'monospace' }} />
              {!form.sku && (form.marca || form.categoria) && (
                <button type="button"
                  onClick={() => setForm(prev => ({...prev, sku: sugerirSku(form.marca, form.categoria, productos)}))}
                  style={{ background: 'transparent', border: 0, color: 'var(--blue, #1a5cb8)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0', textDecoration: 'underline' }}>
                  💡 Usar sugerido: {sugerirSku(form.marca, form.categoria, productos)}
                </button>
              )}
            </Field>
            <Field label="Nombre del producto" required>
              <input type="text" required value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} placeholder="Ej: Cottonmax Multiuso 1L" />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Marca">
              <input type="text" value={form.marca} onChange={e => setForm({...form, marca: e.target.value})} placeholder="Cottonmax" />
            </Field>
            <Field label="Presentación">
              <input type="text" value={form.presentacion} onChange={e => setForm({...form, presentacion: e.target.value})} placeholder="1 L · 500 mL · 12 unid" />
            </Field>
          </div>

          <Field label="Categoría (para que aparezca en la Tienda)" required>
            <select required value={form.categoria} onChange={e => setForm({...form, categoria: e.target.value})}>
              <option value="">— elegir categoría —</option>
              {CATEGORIAS.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </Field>

          <Field label="Tier (calidad/posicionamiento)">
            <select value={form.tier} onChange={e => setForm({...form, tier: e.target.value})}>
              {TIERS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>

          <h3 className="section-head" style={{ marginTop: 18 }}>💰 Precios</h3>
          <div style={{ background: '#f0fcfd', border: '1px solid #b3eef3', padding: '8px 12px', borderRadius: 8, fontSize: 12, color: 'var(--navy-ink)', marginBottom: 10 }}>
            💡 Cargá los precios <b>sin IVA</b>. Definí el IVA del producto. El sistema calcula el precio con IVA automáticamente.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
            <Field label="IVA del producto">
              <select value={form.iva_pct} onChange={e => setForm({...form, iva_pct: Number(e.target.value)})}>
                <option value={22}>22% · General</option>
                <option value={10}>10% · Canasta básica</option>
              </select>
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Costo unitario c/IVA (UYU)">
              <input type="number" min="0" step="0.01" value={form.costo_unitario} onChange={e => setForm({...form, costo_unitario: e.target.value.replace(',', '.')})} placeholder="50.00" />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>= {fmtMontoIva(form.costo_unitario ? Number(form.costo_unitario) / (1 + Number(form.iva_pct || 22)/100) : 0)} s/IVA {form.iva_pct}%</div>
            </Field>
            <Field label="Precio público c/IVA (UYU)">
              <input type="number" min="0" step="0.01" value={form.precio_publico} onChange={e => setForm({...form, precio_publico: e.target.value.replace(',', '.')})} placeholder="100" />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>= {fmtMontoIva(form.precio_publico ? Number(form.precio_publico) / (1 + Number(form.iva_pct || 22)/100) : 0)} s/IVA {form.iva_pct}%</div>
            </Field>
          </div>
          {precioPub > 0 && (
            <div style={{ background: '#f0fcfd', border: '1px solid #b3eef3', padding: '8px 12px', borderRadius: 8, fontSize: 12, color: 'var(--navy-ink)', marginTop: -6, marginBottom: 14 }}>
              💡 Precio mercado se calcula automático: <strong>${precioMercadoSugerido.toLocaleString('es-UY')}</strong> (cliente verá "ahorrás ~12%")
            </div>
          )}

          <h3 className="section-head">📦 Stock</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Stock mínimo">
              <input type="number" min="0" step="1" value={form.stock_minimo} onChange={e => setForm({...form, stock_minimo: e.target.value})} />
            </Field>
            <Field label="Stock crítico">
              <input type="number" min="0" step="1" value={form.stock_critico} onChange={e => setForm({...form, stock_critico: e.target.value})} />
            </Field>
            <Field label="Proveedor">
              <select value={form.proveedor_id} onChange={e => setForm({...form, proveedor_id: e.target.value})}>
                <option value="">— sin proveedor —</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Disponibilidad">
            <label style={{ fontSize: 14 }}>
              <input type="checkbox" checked={form.vendible_suelto} onChange={e => setForm({...form, vendible_suelto: e.target.checked})} style={{ width: 'auto', marginRight: 8 }} />
              Vender suelto en La Tienda (no solo en kits)
            </label>
          </Field>

          {/* Ficha tienda — opcional, también se llena con foto+IA */}
          <div style={{
            background: '#F8F6F1', border: '1px solid #E2E8F0', borderRadius: 10,
            padding: '12px 14px', marginTop: 16,
          }}>
            <button
              type="button"
              onClick={() => setMostrarFicha(v => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700,
                fontSize: 14, color: '#1A3461', width: '100%',
              }}
            >
              <span>{mostrarFicha ? '▼' : '▶'}</span>
              📝 Ficha tienda <span style={{ fontWeight: 400, color: '#64748B', fontSize: 12 }}>(opcional · si subís foto se llena sola con IA)</span>
            </button>

            {mostrarFicha && (
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Field label="Descripción">
                  <textarea
                    value={form.descripcion}
                    onChange={e => setForm({ ...form, descripcion: e.target.value })}
                    placeholder="Ej: Limpiador multiuso con bicarbonato. Levanta grasa y deja brillo en mesadas, vidrios y baño..."
                    rows={3}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </Field>
                <Field label="Beneficios (uno por línea, se muestran como ✓)">
                  <textarea
                    value={form.beneficios}
                    onChange={e => setForm({ ...form, beneficios: e.target.value })}
                    placeholder="Limpia sin rayar&#10;Aroma duradero&#10;Lavable y reutilizable"
                    rows={3}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Field label="Ideal para">
                    <input
                      type="text"
                      value={form.ideal_para}
                      onChange={e => setForm({ ...form, ideal_para: e.target.value })}
                      placeholder="Cocina, baño, vidrios"
                    />
                  </Field>
                  <Field label="Aroma">
                    <input
                      type="text"
                      value={form.aroma}
                      onChange={e => setForm({ ...form, aroma: e.target.value })}
                      placeholder="Limón, lavanda, neutro"
                    />
                  </Field>
                </div>
                <Field label="Modo de uso">
                  <textarea
                    value={form.modo_uso}
                    onChange={e => setForm({ ...form, modo_uso: e.target.value })}
                    placeholder="Aplicar sobre la superficie, frotar con paño húmedo y enjuagar."
                    rows={2}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Field label="Composición">
                    <input
                      type="text"
                      value={form.composicion}
                      onChange={e => setForm({ ...form, composicion: e.target.value })}
                      placeholder="100% Viscosa · Con bicarbonato"
                    />
                  </Field>
                  <Field label="Rendimiento">
                    <input
                      type="text"
                      value={form.rendimiento}
                      onChange={e => setForm({ ...form, rendimiento: e.target.value })}
                      placeholder="24 lavadas · 1 mes uso normal"
                    />
                  </Field>
                </div>
              </div>
            )}
          </div>

          {err && <div className="err-box">{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn primary" disabled={saving}>
              {saving ? 'Creando...' : 'Crear y cargar ficha →'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CompraModal({ producto, proveedores, onClose, onDone }) {
  const [cantidad, setCantidad] = useState('')
  const [costo, setCosto] = useState(producto.costo_unitario)
  const [proveedorId, setProveedorId] = useState(producto.proveedor?.id || '')
  const [factura, setFactura] = useState('')
  const [notas, setNotas] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  // Preview PPP en tiempo real
  const stockActual = Number(producto.stock_actual)
  const costoActual = Number(producto.costo_unitario)
  const cant = parseFloat(cantidad) || 0
  const costoN = parseFloat(costo) || 0
  const stockNuevo = stockActual + cant
  const costoPPP = stockNuevo > 0
    ? Math.round(((stockActual * costoActual + cant * costoN) / stockNuevo) * 100) / 100
    : costoN
  const diff = costoPPP - costoActual
  const diffPct = costoActual > 0 ? (diff / costoActual) * 100 : 0

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null); setSaving(true)
    try {
      await registrarCompra({
        productoId: producto.id,
        cantidad: parseFloat(cantidad),
        costoUnitario: parseFloat(costo),
        proveedorId: proveedorId || null,
        factura, notas
      })
      onDone()
    } catch (e) {
      setErr(e.message)
    } finally { setSaving(false) }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginBottom: 6 }}>Registrar compra</h2>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 8 }}>
        <code style={{ background: 'var(--paper)', padding: '2px 6px', borderRadius: 4 }}>{producto.sku}</code> · {producto.nombre} · {producto.presentacion}
      </p>
      <div style={{ background: 'var(--paper)', padding: '8px 12px', borderRadius: 6, fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
        Stock actual: <strong style={{ color: 'var(--ink)' }}>{stockActual}</strong> · Costo actual: <strong style={{ color: 'var(--ink)' }}>${costoActual.toFixed(2)}</strong>
      </div>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Cantidad" required>
            <input type="number" min="0.01" step="0.01" required value={cantidad} onChange={e => setCantidad(e.target.value)} placeholder="20" />
          </Field>
          <Field label="Costo unitario nuevo (UYU)" required>
            <input type="number" min="0" step="0.01" required value={costo} onChange={e => setCosto(e.target.value)} />
          </Field>
        </div>
        {cant > 0 && (
          <div style={{ background: Math.abs(diffPct) > 10 ? '#FEF3C7' : '#ECFDF5', border: `1px solid ${Math.abs(diffPct) > 10 ? '#F59E0B' : '#10B981'}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 }}>
            📊 <strong>Después de la compra:</strong> stock {stockNuevo} · costo PPP <strong>${costoPPP.toFixed(2)}</strong>
            {diff !== 0 && (
              <span style={{ marginLeft: 6, color: diff > 0 ? '#92400E' : '#065F46', fontWeight: 700 }}>
                ({diff > 0 ? '+' : ''}{diffPct.toFixed(1)}%)
              </span>
            )}
            {Math.abs(diffPct) > 10 && <div style={{ marginTop: 4, fontSize: 11, color: '#92400E' }}>⚠️ Cambio de costo &gt; 10%, revisá si es correcto</div>}
          </div>
        )}
        <Field label="Proveedor">
          <select value={proveedorId} onChange={e => setProveedorId(e.target.value)}>
            <option value="">— ninguno —</option>
            {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
        </Field>
        <Field label="Nro factura (opcional)">
          <input type="text" value={factura} onChange={e => setFactura(e.target.value)} placeholder="A-001-0012345" />
        </Field>
        <Field label="Notas">
          <textarea value={notas} onChange={e => setNotas(e.target.value)} rows="2" />
        </Field>
        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Guardando...' : 'Registrar compra'}</button>
        </div>
      </form>
    </Modal>
  )
}

function AjusteModal({ producto, onClose, onDone }) {
  const [cantidadNueva, setCantidadNueva] = useState(producto.stock_actual)
  const [motivo, setMotivo] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null); setSaving(true)
    try {
      await ajustarStock({
        productoId: producto.id,
        cantidadNueva: parseFloat(cantidadNueva),
        motivo
      })
      onDone()
    } catch (e) {
      setErr(e.message)
    } finally { setSaving(false) }
  }

  const diff = parseFloat(cantidadNueva) - parseFloat(producto.stock_actual)

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginBottom: 6 }}>Ajustar stock</h2>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 18 }}>
        <code>{producto.sku}</code> · {producto.nombre}<br />
        Stock actual: <strong>{producto.stock_actual}</strong>
      </p>
      <form onSubmit={handleSubmit}>
        <Field label="Stock real (después de contar físico)" required>
          <input type="number" min="0" step="0.01" required value={cantidadNueva} onChange={e => setCantidadNueva(e.target.value)} />
        </Field>
        {!isNaN(diff) && diff !== 0 && (
          <div style={{ padding: '8px 12px', background: diff > 0 ? '#D1FAE5' : '#FEE2E2', color: diff > 0 ? '#065F46' : '#991B1B', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
            Diferencia: <strong>{diff > 0 ? '+' : ''}{diff.toFixed(2)}</strong>
          </div>
        )}
        <Field label="Motivo (rotura, error de carga, etc.)" required>
          <input type="text" required value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej: rotura 2 botellas" />
        </Field>
        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Guardando...' : 'Ajustar'}</button>
        </div>
      </form>
    </Modal>
  )
}

function Modal({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children, required }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>
        {label} {required && <span style={{ color: 'var(--err)' }}>*</span>}
      </b>
      {children}
    </label>
  )
}

/* Badge compacto del estado de la ficha tienda + tooltip con quien la cargó */
function FichaBadge({ producto }) {
  const ok = fichaCargadaOK(producto)
  const fechaIso = producto?.ficha_actualizada_at
  const por = producto?.ficha_por?.nombre
  const fechaCorta = fechaIso ? new Date(fechaIso).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' }) : null
  const tooltip = ok
    ? (fechaIso ? `Cargada el ${new Date(fechaIso).toLocaleString('es-UY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}${por ? ` por ${por}` : ''}` : 'Ficha cargada')
    : 'Falta descripción y/o beneficios'
  return (
    <div title={tooltip} style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
        padding: '2px 8px', borderRadius: 999, fontWeight: 800, letterSpacing: '.04em',
        background: ok ? '#D1FAE5' : '#FEF2F2', color: ok ? '#065F46' : '#991B1B',
        textTransform: 'uppercase', fontSize: 10,
      }}>
        {ok ? '✓ OK' : '⚠ Falta'}
      </span>
      {ok && (fechaCorta || por) && (
        <span style={{ color: '#64748b', fontSize: 10, lineHeight: 1.3 }}>
          {fechaCorta && <>{fechaCorta}</>}
          {por && <> · {por.split(' ')[0]}</>}
        </span>
      )}
    </div>
  )
}

// ════ RowMenu — dropdown compacto para las acciones de cada fila ════
// Reemplaza los 4-5 botones apilados por 1 botón ⋯ + menú desplegable.
// NO cambia la lógica: cada item llama exactamente a los mismos handlers que antes.
function RowMenu({ producto, onCompra, onAjustar, onPausar, onReanudar, onBorrar, onRestaurar }) {
  const [open, setOpen] = useState(false)
  // openUpward = si no hay espacio suficiente abajo, el menú se abre hacia arriba.
  // Fix 6-jul-2026: antes se cortaba en filas cercanas al bottom de la tabla.
  const [openUpward, setOpenUpward] = useState(false)
  const ref = useRef(null)
  const btnRef = useRef(null)

  useEffect(() => {
    if (!open) return
    function close(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', e => { if (e.key === 'Escape') setOpen(false) })
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  // Al abrir, calcular si hay espacio abajo. Menú alto ~280px (6 items + separador + padding).
  // Fix 7-jul-2026: buffer más grande + fallback al lado con más espacio si ninguno alcanza.
  useEffect(() => {
    if (!open || !btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    const espacioAbajo = window.innerHeight - rect.bottom
    const espacioArriba = rect.top
    const alturaMenu = 280
    if (espacioAbajo >= alturaMenu) {
      setOpenUpward(false)
    } else if (espacioArriba >= alturaMenu) {
      setOpenUpward(true)
    } else {
      // Ninguno alcanza — elegir el que tenga más espacio.
      setOpenUpward(espacioArriba > espacioAbajo)
    }
  }, [open])

  const p = producto
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        title="Acciones"
        style={{
          width: 28, height: 28, borderRadius: 6, border: 'none',
          background: open ? '#1A3461' : 'transparent',
          color: open ? '#fff' : '#64748B',
          fontSize: 16, fontWeight: 700, lineHeight: 1,
          cursor: 'pointer', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
        }}
        onMouseEnter={e => { if (!open) { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = '#0F172A' } }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748B' } }}
      >⋯</button>

      {open && (
        <div style={{
          position: 'absolute',
          right: 0,
          // openUpward: si estamos cerca del bottom del viewport, abrir hacia arriba.
          ...(openUpward
            ? { bottom: '100%', marginBottom: 4 }
            : { top: '100%', marginTop: 4 }),
          background: '#fff',
          border: '1px solid #E2E8F0',
          borderRadius: 8,
          boxShadow: '0 6px 24px rgba(15,23,42,0.12)',
          padding: 4,
          minWidth: 175,
          zIndex: 999,  // Fix 7-jul-2026: subido de 50 para no quedar tapado
          fontSize: 12.5,
        }}>
          {p.deleted_at ? (
            <button
              type="button"
              onClick={() => { setOpen(false); onRestaurar(p) }}
              style={menuItem('#10B981')}
            >♻ Restaurar producto</button>
          ) : (
            <>
              <button type="button" onClick={() => { setOpen(false); onCompra(p) }} style={menuItem()}>
                <span style={menuIco()}>＋</span>Registrar compra
              </button>
              <button type="button" onClick={() => { setOpen(false); onAjustar(p) }} style={menuItem()}>
                <span style={menuIco()}>≡</span>Ajustar stock
              </button>
              <div style={{ height: 1, background: '#E2E8F0', margin: '4px 0' }} />
              {p.pausado ? (
                <button type="button" onClick={() => { setOpen(false); onReanudar(p) }} style={menuItem('#065F46')}>
                  <span style={menuIco()}>▶</span>Reanudar
                </button>
              ) : (
                <button type="button" onClick={() => { setOpen(false); onPausar(p) }} style={menuItem('#92400E')}>
                  <span style={menuIco()}>⏸</span>Pausar
                </button>
              )}
              <button type="button" onClick={() => { setOpen(false); onBorrar(p) }} style={menuItem('#DC2626')}>
                <span style={menuIco()}>🗑</span>Borrar
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
function menuItem(color) {
  return {
    display: 'flex', alignItems: 'center', gap: 8,
    width: '100%', textAlign: 'left',
    background: 'transparent', border: 'none',
    padding: '7px 10px', borderRadius: 4,
    cursor: 'pointer',
    color: color || '#0F172A',
    fontWeight: 500, fontSize: 12.5,
    fontFamily: 'inherit',
  }
}
function menuIco() {
  return { width: 14, color: '#94A3B8', fontSize: 13, display: 'inline-block', textAlign: 'center' }
}
